/**
 * Разделы приложения — одни для вкладок шапки и нижней панели телефона.
 *
 * Какие разделы видны при каких правах, решает @taskflow/client-core —
 * одинаково для веба и мобильного приложения. Здесь к ним добавляются
 * адреса, значки и подсветка.
 */

import { type SectionKey, useVisibleSections } from "@taskflow/client-core";
import { ChartColumn, NotebookPen, ShieldCheck, SquareKanban, TreePalm, type LucideIcon } from "lucide-react";
import { useLocation } from "react-router-dom";

import { taskbookLink } from "./taskbookLink";

export interface AppSection {
  /** Ключ раздела; у телефона есть ещё «profile» — вне общего перечня. */
  key: string;
  to: string;
  label: string;
  /** Подпись в нижней панели телефона: там на вкладку ~90 px. */
  shortLabel: string;
  icon: LucideIcon;
  /** Адреса, на которых вкладка подсвечена. */
  sections: string[];
  /** Уже на месте: нажатие не должно пересоздавать экран. */
  stay: boolean;
}

const ICONS: Record<SectionKey, LucideIcon> = {
  tasks: SquareKanban,
  reports: ChartColumn,
  notes: NotebookPen,
  vacations: TreePalm,
  admin: ShieldCheck,
};

export function useAppSections(): AppSection[] {
  const location = useLocation();
  const taskbook = taskbookLink(location.pathname, location.search);
  return useVisibleSections().map(({ key, label, shortLabel }) => {
    // Задачник открывает последнюю доску, а не список досок.
    const route =
      key === "tasks"
        ? { to: taskbook.to, sections: ["/tasks", "/boards"], stay: taskbook.stay }
        : { to: `/${key}`, sections: [`/${key}`], stay: false };
    return { key, label, shortLabel, icon: ICONS[key], ...route };
  });
}

export function isSectionActive(pathname: string, sections: string[]): boolean {
  return sections.some((section) => pathname === section || pathname.startsWith(`${section}/`));
}
